import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usersAPI, auditAPI } from '../../api/client';
import { getUserPhotoUrl } from '../../lib/fileUrls';
import UserAvatar, { getAvatarColor } from '../../components/UserAvatar';
import ChangeStatusModal, { USER_STATUS_CONFIG, UserStatusBadge, getUserStatus } from '../../components/ChangeStatusModal';
import { ArrowLeft, Edit2, Key, Camera, Phone, Mail, Calendar, Clock, AlertTriangle, X, Upload } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../api/client';

const ROLE_LABELS = { super_admin: 'Super Admin', lider_proyecto: 'Líder de Proyecto', levantador: 'Levantador', cliente: 'Cliente' };
const BLOOD_TYPES = ['A+','A-','B+','B-','AB+','AB-','O+','O-'];

function TabContent({ user, reload }) {
  const [activeTab, setActiveTab] = useState('info');
  const [auditLogs, setAuditLogs] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (activeTab === 'actividad') {
        auditAPI.list({ limit: 100 }).then(r => {
          // Filter client-side by user email since API doesn't support user filter
          const all = r.data.items || [];
          const filtered = user?.email
            ? all.filter(log => log.user_email === user.email)
            : all.slice(0, 20);
          setAuditLogs(filtered.slice(0, 20));
        }).catch(() => {});
      }
  }, [activeTab]);

  return (
    <>
      <div className="flex gap-1 border-b border-slate-200">
        {['info', 'emergencia', 'seguridad', 'actividad'].map(t => (
          <button key={t} onClick={() => setActiveTab(t)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors capitalize ${activeTab === t ? 'border-soem-primary text-soem-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${t}`}>
            {t === 'info' ? 'Información General' : t === 'emergencia' ? 'Contacto Emergencia' : t === 'seguridad' ? 'Seguridad Social' : 'Actividad'}
          </button>
        ))}
      </div>

      {activeTab === 'info' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            {[
              { label: 'Nombre(s)', value: user.first_name || user.name },
              { label: 'Apellidos', value: user.last_name },
              { label: 'Email', value: user.email, icon: Mail },
              { label: 'Teléfono', value: user.phone, icon: Phone },
              { label: 'Rol', value: ROLE_LABELS[user.role] },
              { label: 'Jefe directo', value: user.direct_supervisor_name || '—' },
              { label: 'Creado', value: user.created_at ? new Date(user.created_at).toLocaleDateString('es-MX') : '—', icon: Calendar },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label}>
                <p className="soem-label mb-0.5 flex items-center gap-1">{Icon && <Icon size={10} />} {label}</p>
                <p className="text-sm font-medium text-soem-navy">{value || '—'}</p>
              </div>
            ))}
          </div>
          <div className="space-y-3">
            <div>
              <p className="soem-label mb-0.5">Estado actual</p>
              <UserStatusBadge user={user} />
            </div>
            {getUserStatus(user) === 'temporal' && user.temporary_access_expires_at && (
              <div className="bg-orange-50 border border-orange-200 rounded-sm p-3">
                <p className="soem-label text-orange-700 mb-1 flex items-center gap-1"><Clock size={10} /> Vencimiento de acceso temporal</p>
                <p className="font-bold text-orange-800">{new Date(user.temporary_access_expires_at).toLocaleString('es-MX')}</p>
              </div>
            )}
            {user.failed_login_attempts > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-sm p-2">
                <p className="text-xs text-orange-700 font-semibold flex items-center gap-1">
                  <AlertTriangle size={11} /> {user.failed_login_attempts} intento(s) de login fallido(s)
                </p>
              </div>
            )}
            <div>
              <p className="soem-label mb-0.5 flex items-center gap-1"><Clock size={10} /> Último acceso</p>
              <p className="text-sm text-slate-600">{user.last_login_at ? new Date(user.last_login_at).toLocaleString('es-MX') : '—'}</p>
            </div>
            {user.subordinates_count > 0 && (
              <div>
                <p className="soem-label mb-0.5">Subordinados directos</p>
                <p className="text-sm font-semibold text-soem-navy">{user.subordinates_count} personas</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'emergencia' && (
        <div className="max-w-md space-y-3">
          {user.emergency_contact ? (
            <>
              {[
                { label: 'Nombre del contacto', value: user.emergency_contact.name },
                { label: 'Parentesco', value: user.emergency_contact.relationship },
                { label: 'Teléfono', value: user.emergency_contact.phone },
                { label: 'Email', value: user.emergency_contact.email },
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="soem-label mb-0.5">{label}</p>
                  <p className="text-sm font-medium text-soem-navy">{value || '—'}</p>
                </div>
              ))}
              <button onClick={() => navigate(`/dashboard/usuarios/${user.id}/editar`)}
                className="soem-btn-secondary px-4 py-2 text-sm mt-2">
                Editar contacto de emergencia
              </button>
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-slate-400 mb-3">Sin contacto de emergencia registrado.</p>
              <button onClick={() => navigate(`/dashboard/usuarios/${user.id}/editar`)}
                className="soem-btn-primary px-4 py-2 text-sm">Agregar contacto</button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'seguridad' && (
        <div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-3 flex items-center gap-2 mb-4">
            <AlertTriangle size={15} className="text-yellow-600" />
            <p className="text-yellow-800 text-xs">Esta información es confidencial y solo visible para el administrador y el propio usuario.</p>
          </div>
          {user.social_security ? (
            <div className="max-w-md space-y-3">
              <div>
                <p className="soem-label mb-0.5">Número IMSS</p>
                <p className="text-sm font-medium font-mono text-soem-navy">{user.social_security.imss_number || '—'}</p>
              </div>
              <div>
                <p className="soem-label mb-0.5">Tipo de sangre</p>
                {user.social_security.blood_type ? (
                  <span className="bg-red-100 text-red-800 border border-red-200 text-sm font-bold px-3 py-1 rounded-full">
                    {user.social_security.blood_type}
                  </span>
                ) : <span className="text-slate-400 text-sm">—</span>}
              </div>
              {user.social_security.allergies && (
                <div>
                  <p className="soem-label mb-0.5">Alergias</p>
                  <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded border">{user.social_security.allergies}</p>
                </div>
              )}
              {user.social_security.medical_notes && (
                <div>
                  <p className="soem-label mb-0.5">Notas médicas</p>
                  <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded border">{user.social_security.medical_notes}</p>
                </div>
              )}
              <button onClick={() => navigate(`/dashboard/usuarios/${user.id}/editar`)}
                className="soem-btn-secondary px-4 py-2 text-sm">Editar datos médicos</button>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-slate-400 mb-3">Sin datos de seguridad social.</p>
              <button onClick={() => navigate(`/dashboard/usuarios/${user.id}/editar`)}
                className="soem-btn-primary px-4 py-2 text-sm">Agregar datos</button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'actividad' && (
        <div>
          {auditLogs.length === 0 ? (
            <p className="text-slate-400 text-sm py-8 text-center">Sin actividad registrada.</p>
          ) : (
            <div className="soem-card overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="soem-table-header">
                  <th className="px-4 py-3 text-left">Fecha/Hora</th>
                  <th className="px-4 py-3 text-left">Acción</th>
                  <th className="px-4 py-3 text-left">Módulo</th>
                </tr></thead>
                <tbody>
                  {auditLogs.map(log => (
                    <tr key={log.id} className="soem-table-row">
                      <td className="px-4 py-2.5 text-xs font-mono text-slate-400">
                        {new Date(log.timestamp).toLocaleString('es-MX')}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium">{log.action}</span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-500">{log.entity || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default function UserAdminProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusModal, setStatusModal] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoRef = useRef(null);

  const load = async () => {
    try {
      const r = await usersAPI.get(id);
      setUser(r.data);
    } catch { toast.error('Error al cargar usuario'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      await usersAPI.uploadPhoto(id, file);
      toast.success('Foto actualizada');
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Error al subir foto');
    } finally { setUploadingPhoto(false); }
  };

  const handleResetPwd = async () => {
    try {
      await apiClient.post('/auth/forgot-password', { email: user.email });
      toast.success(`Instrucciones enviadas a ${user.email}`);
    } catch { toast.error('Error al enviar email'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" /></div>;
  if (!user) return <div className="text-center py-12 text-slate-400">Usuario no encontrado.</div>;

  const roleCfg = { super_admin: 'bg-purple-100 text-purple-800', lider_proyecto: 'bg-blue-100 text-blue-800', levantador: 'bg-green-100 text-green-800', cliente: 'bg-orange-100 text-orange-800' };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="user-admin-profile">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button onClick={() => navigate('/dashboard/usuarios')} className="soem-btn-secondary p-2 mt-1" aria-label="Volver a usuarios"><ArrowLeft size={18} /></button>
        <div className="flex flex-1 items-start gap-5">
          {/* Avatar with camera overlay */}
          <div className="relative group">
            <UserAvatar user={user} size="xl" />
            <button onClick={() => photoRef.current?.click()}
              className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white"
              disabled={uploadingPhoto}>
              {uploadingPhoto ? <span className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" /> : <Camera size={20} />}
            </button>
            <input ref={photoRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={handlePhotoUpload} />
          </div>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold text-soem-navy">{user.full_name || user.name}</h1>
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${roleCfg[user.role] || 'bg-slate-100 text-slate-600'}`}>
                {ROLE_LABELS[user.role]}
              </span>
              <UserStatusBadge user={user} />
            </div>
            {user.last_login_at && (
              <p className="text-slate-400 text-xs mt-1 flex items-center gap-1">
                <Clock size={10} /> Último ingreso: {new Date(user.last_login_at).toLocaleString('es-MX')}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={() => setStatusModal(true)}
            className="soem-btn-secondary px-3 py-2 text-sm flex items-center gap-2">
            <Clock size={14} /> Estado
          </button>
          <button onClick={handleResetPwd}
            className="soem-btn-secondary px-3 py-2 text-sm flex items-center gap-2">
            <Key size={14} /> Restablecer contraseña
          </button>
          <button onClick={() => navigate(`/dashboard/usuarios/${id}/editar`)}
            className="soem-btn-primary px-4 py-2 text-sm flex items-center gap-2" data-testid="edit-user-btn">
            <Edit2 size={14} /> Editar
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="soem-card p-6 space-y-6">
        <TabContent user={user} reload={load} />
      </div>

      {statusModal && (
        <ChangeStatusModal user={user} onChanged={load} onClose={() => setStatusModal(false)} />
      )}
    </div>
  );
}
